/**
 * <reviews-judgeme> — lädt Reviews aus assets/judgeme-reviews.json
 * (statisch via Build-Script `npm run sync-reviews` aktualisiert).
 * Rendert Summary-Card (avg + stars + caption + bars) UND Review-Cards.
 */

(() => {
  if (customElements.get('reviews-judgeme')) return;

  function initials(name) {
    if (!name) return '??';
    const parts = String(name).trim().split(/\s+/);
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || parts[0]?.[1] || '');
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function starsHtml(rating) {
    let html = '';
    for (let i = 1; i <= 5; i++) {
      const off = i > rating ? ' reviews-featured__star--off' : '';
      html += `<svg viewBox="0 0 256 256" aria-hidden="true" class="${off}"><path d="M239.2,97.29a16,16,0,0,0-13.81-11L166,81.17,142.72,25.81a15.95,15.95,0,0,0-29.44,0L90.07,81.17,30.61,86.32a16,16,0,0,0-9.11,28.06L66.61,153.8,53.09,212.34a16,16,0,0,0,23.84,17.34l51-31,51.11,31a16,16,0,0,0,23.84-17.34l-13.51-58.6,45.1-39.36A16,16,0,0,0,239.2,97.29Z"/></svg>`;
    }
    return html;
  }

  function cardHtml(r, verifiedLabel) {
    const rating = Number(r.rating) || 5;
    const body = (r.body || r.title || '').trim();
    return `
      <li class="reviews-featured__card">
        <span class="reviews-featured__card-stars" aria-label="${rating} von 5 Sternen">${starsHtml(rating)}</span>
        ${body ? `<p class="reviews-featured__card-quote">${escapeHtml(body)}</p>` : ''}
        <div class="reviews-featured__card-author">
          <span class="reviews-featured__card-avatar" aria-hidden="true">${escapeHtml(initials(r.reviewer)).toUpperCase()}</span>
          <div class="reviews-featured__card-author-meta">
            <span class="reviews-featured__card-name">${escapeHtml(r.reviewer || '—')}</span>
            ${r.verified ? `<span class="reviews-featured__card-verified">${escapeHtml(verifiedLabel)}</span>` : ''}
          </div>
        </div>
      </li>
    `;
  }

  let dataPromise = null;
  function loadData(source) {
    if (!dataPromise) dataPromise = fetch(source, { cache: 'force-cache' }).then((r) => r.json());
    return dataPromise;
  }

  class ReviewsJudgeme extends HTMLElement {
    connectedCallback() {
      this.source = this.dataset.source;
      this.productId = this.dataset.productId || '';
      this.grid = this.querySelector('[data-reviews-grid]');
      this.errorEl = this.querySelector('[data-error]');
      this.verifiedLabel = (document.documentElement.lang || 'de').startsWith('de') ? 'Verifizierter Kauf' : 'Verified buyer';
      if (!this.source) {
        this.#showError();
        return;
      }
      this.#observe();
    }

    #observe() {
      if (!('IntersectionObserver' in window)) return this.#load();
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              io.disconnect();
              this.#load();
            }
          });
        },
        { rootMargin: '200px' }
      );
      io.observe(this);
    }

    async #load() {
      try {
        const data = await loadData(this.source);
        this.#renderSummary(data);
        this.#renderCards(data);
      } catch (err) {
        console.error('[reviews-judgeme]', err);
        this.#showError();
      }
    }

    #renderSummary(data) {
      let reviews = data.reviews || [];
      if (this.productId) {
        reviews = reviews.filter((r) => String(r.product_id) === String(this.productId));
      }

      const count = reviews.length;
      const avg = count ? Math.round((reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / count) * 10) / 10 : 0;

      let distribution;
      if (!this.productId && data.distribution) {
        distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0, ...data.distribution };
      } else {
        distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        for (const r of reviews) {
          const k = Math.max(1, Math.min(5, Math.round(Number(r.rating) || 0)));
          distribution[k] = (distribution[k] || 0) + 1;
        }
      }

      const avgEl = this.querySelector('[data-summary-avg]');
      if (avgEl) avgEl.textContent = String(avg).replace('.', ',');

      const captionEl = this.querySelector('[data-summary-caption]');
      if (captionEl) {
        captionEl.textContent = count === 1
          ? 'basiert auf 1 Bewertung'
          : `basiert auf ${count.toLocaleString('de-DE')} Bewertungen`;
      }

      const filled = Math.round(avg);
      const starsEl = this.querySelector('[data-summary-stars]');
      if (starsEl) {
        starsEl.querySelectorAll('svg').forEach((svg, i) => {
          svg.classList.toggle('reviews-summary__star--off', i + 1 > filled);
        });
      }

      const max = Math.max(...Object.values(distribution).map(Number));
      for (let star = 5; star >= 1; star--) {
        const c = Number(distribution[star]) || 0;
        const pct = max > 0 ? (c / max) * 100 : 0;
        const fill = this.querySelector(`[data-summary-bar="${star}"]`);
        const cnt = this.querySelector(`[data-summary-count="${star}"]`);
        if (fill) fill.style.width = `${pct}%`;
        if (cnt) cnt.textContent = c.toLocaleString('de-DE');
      }
    }

    #renderCards(data) {
      if (!this.grid) return;
      const display = parseInt(this.dataset.display, 10) || 6;
      const minRating = parseInt(this.dataset.minRating, 10) || 1;
      const verifiedOnly = this.hasAttribute('data-verified-only');

      const reviews = (data.reviews || [])
        .filter((r) => Number(r.rating) >= minRating)
        .filter((r) => !verifiedOnly || r.verified)
        .filter((r) => !this.productId || String(r.product_id) === String(this.productId))
        .slice(0, display);

      if (!reviews.length) {
        this.#showError('Keine passenden Reviews gefunden.');
        return;
      }

      this.grid.innerHTML = reviews.map((r) => cardHtml(r, this.verifiedLabel)).join('');
      this.grid.setAttribute('aria-busy', 'false');
    }

    #showError(msg) {
      if (this.errorEl) {
        if (msg) this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }
      if (this.grid) {
        this.grid.innerHTML = '';
        this.grid.setAttribute('aria-busy', 'false');
      }
    }
  }

  customElements.define('reviews-judgeme', ReviewsJudgeme);
})();
